/**
 * Aufbereitung der Ergebnis-Details (Prüfschritte) einer Prüfregel für die Anzeige.
 *
 * Die eigentliche Feldauflösung liegt in `report-display-config.ts` ({@link resolveDetailEntries});
 * hier steht die Logik darüber: welche Detail-Spalten konfiguriert sind und wie die aufgelösten
 * Einträge in „variiert je Prüfschritt" (KI-generiert) und „regelweit konstant" (Prüfparameter)
 * aufgeteilt werden. Rein, ohne Angular – gemeinsam genutzt von Spalten- und Kartenansicht.
 */

import {ReportDetail} from './normalized-report.model';
import {isAnswerTextKey, ReportDisplayConfig, ResolvedDetailEntry, resolveDetailEntries} from './report-display-config';

/** Eine konfigurierte Detail-Spalte (Schlüssel = benutzerdefinierter Ausgabeparameter). */
export interface DetailColumn {
  key: string;
  label?: string;
  perStep?: boolean;
}

/**
 * Konfigurierte, sichtbare Detail-Spalten. Leer, wenn keine Konfiguration vorliegt – dann greift
 * in {@link resolveDetailEntries} der generische Fallback über alle vorhandenen KI-Felder, damit
 * ein Altbericht nichts verwirft.
 */
export function resolveDetailColumns(config: ReportDisplayConfig | null | undefined): DetailColumn[] {
  const columns = config?.levels?.find(level => level.key === 'detail')?.columns;
  if (!columns || columns.length === 0) return [];
  return columns
    .filter(column => column.visible !== false)
    .map(column => ({key: column.key, label: column.label, perStep: column.perStep}));
}

/**
 * Feldschlüssel (lowercase), die über ALLE Prüfschritte einer Regel denselben Wert haben.
 * Prüfparameter/Eigener Wert sind bauartbedingt regelweit konstant, KI-generierte Felder variieren
 * pro Schritt. Datengetrieben – unabhängig von einer evtl. veralteten Spaltenkonfiguration. Bei nur
 * einem Prüfschritt ist keine Unterscheidung möglich (leeres Ergebnis).
 */
export function resolveConstantFieldKeys(details: ReadonlyArray<ReportDetail>): Set<string> {
  const keys = new Set<string>();
  if (details.length < 2) return keys;

  const allKeys = new Set<string>();
  for (const detail of details) {
    for (const key of Object.keys(detail.fields ?? {})) allKeys.add(key);
  }
  for (const key of allKeys) {
    const values = details.map(detail => {
      const value = (detail.fields ?? {})[key];
      return value === undefined ? ' __undef__' : JSON.stringify(value);
    });
    if (values.every(value => value === values[0])) keys.add(key.toLowerCase());
  }
  return keys;
}

function isConstantEntry(entry: ResolvedDetailEntry, constantKeys: ReadonlySet<string>): boolean {
  if (entry.perStep === false) return true;
  // Explizit als Prüfschritt-Feld konfiguriert (z. B. KI-Generiert): der datengetriebene
  // Abgleich darf das nicht überstimmen, sonst landet das Feld fälschlich bei den
  // Regelparametern, sobald die KI zufällig für mehrere Schritte denselben Text liefert.
  if (entry.perStepExplicit) return false;
  return constantKeys.has((entry.key ?? '').toLowerCase());
}

/**
 * Teilt die aufgelösten Einträge eines Prüfschritts in die pro Schritt variierenden und die
 * regelweit konstanten auf.
 *
 * Das KI-Freitext-Antwortfeld verliert dabei sein Label: der Prüfschritt-Titel (`detail.heading`)
 * steht bereits als Überschrift über dem Kasten, eine zusätzliche fette Zeile „Antwort" wäre
 * doppelt. Die übrigen Felder (z. B. „Handlungsempfehlung") behalten ihr Label.
 */
export function splitDetailEntries(
  detail: ReportDetail,
  columns: ReadonlyArray<DetailColumn>,
  constantKeys: ReadonlySet<string>,
): { perStep: ResolvedDetailEntry[]; constant: ResolvedDetailEntry[] } {
  const entries = resolveDetailEntries(detail, columns as DetailColumn[]);
  const perStep: ResolvedDetailEntry[] = [];
  const constant: ResolvedDetailEntry[] = [];
  for (const entry of entries) {
    if (isConstantEntry(entry, constantKeys)) {
      constant.push(entry);
    } else {
      perStep.push(isAnswerTextKey(entry.key) ? {...entry, label: ''} : entry);
    }
  }
  return {perStep, constant};
}

/**
 * Ob ein Detail als Facetten-Zeile dargestellt wird (statt als Prüfschritt-Kasten).
 *
 * Datengetrieben statt über den Prüfungstyp: die Facettenform ist die strukturierte Darstellung
 * eines Einzelergebnisses – IDS-Anforderungen nutzen sie, ebenso die Regelergebnisse des
 * BIM-Basis-Checks innerhalb einer Hybrid-Prüfung. Ein Typ-Vergleich hätte den zweiten Fall nicht
 * erfasst, weil dessen Bericht als Ganzes eine Hybrid-Prüfung ist.
 */
export function isFacetDetail(detail: ReportDetail): boolean {
  return !!detail.facetLabel
    || (detail.valueChips?.length ?? 0) > 0
    || detail.percent != null;
}

/**
 * Ob das rohe JSON des Prüfregeldurchlaufs gezeigt wird. KI und Hybrid werden aufbereitet
 * dargestellt (Ergebnis-Felder/Karten), für sie ist das Roh-JSON Rauschen. IDS ebenso: dort ist
 * die Facettenzeile die strukturierte Darstellung – der IDS-Adapter füllt `rawOutput` zwar auf
 * jedem Requirement (check-report-adapter.ts), es darf aber nie angezeigt werden. Das eigentliche
 * Gate dafür ist datengetrieben (eine Facette bekommt ohnehin nie einen Roh-Block, siehe
 * `report-card-model.ts`); der Typ-Ausschluss hier ist die zweite Absicherung.
 */
export function shouldShowRawOutput(checkType: string | null | undefined): boolean {
  const type = (checkType ?? '').toUpperCase();
  return type !== 'AI' && type !== 'KI' && type !== 'HOAI' && type !== 'HYBRID' && type !== 'IDS';
}

/**
 * Kurzform der Kennzahl eines Prüfschritts: die Anzahl betroffener Bauteile (IDS).
 *
 * KI-Bewertungen kommen ausschließlich aus den Ausgabefeldern (`fields`) und werden über
 * `resolveDetailEntries` als reguläre Spalte dargestellt – sie hier zusätzlich auszugeben,
 * hätte die Bewertung doppelt gerendert.
 */
export function formatDetailRating(detail: ReportDetail): string | null {
  if (detail.count > 0) {
    return `${detail.count} Bauteile`;
  }
  return null;
}
